import { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import NextLink, { LinkProps } from 'next/link'
import { NextRouter, useRouter } from 'next/router'
import { useRouter as useNprogressRouter } from 'next-nprogress-bar'
import { forwardRef, PropsWithChildren } from 'react'

import getCookie from '@src/quince-site-common/src/utils/getCookie'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import safeParse from '@src/quince-site-common/src/utils/safeParse'

function isPlatformFlux(to: string) {
  const platformIgnorePathsCookie = getCookie('platform_ignore_paths') ?? ''
  const temporalSSGPaths = safeParse<string[]>(platformIgnorePathsCookie, [])
  const baseUrl = window.location.href
  const pathname = new URL(to, baseUrl).pathname
  return temporalSSGPaths.includes(pathname)
}

function shouldFullReload(to: string): boolean {
  return isBrowser() && isPlatformFlux(to)
}
export const Link = forwardRef<HTMLAnchorElement, PropsWithChildren<LinkProps>>(({ href, ...restProps }, ref) => {
  return shouldFullReload(href as string) ? (
    <a href={href as string | undefined} {...restProps} />
  ) : (
    <NextLink ref={ref} href={href} {...restProps} prefetch={false} />
  )
})
Link.displayName = 'CustomLink'

export function useQuinceNprogressRouter() {
  const router = useNprogressRouter()

  const push = (to: string, options?: NavigateOptions) => {
    if (shouldFullReload(to)) window.location.href = to
    else router.push(to, options)
  }

  const replace = (to: string, options?: NavigateOptions) => {
    if (shouldFullReload(to)) window.location.href = to
    else router.replace(to, options)
  }

  return {
    ...router,
    push,
    replace,
  }
}

export interface QuinceRouter extends Omit<NextRouter, 'push' | 'replace'> {
  push: (to: string, options?: { shallow?: boolean; scroll?: boolean }) => void
  replace: (to: string, as?: string, options?: { shallow?: boolean; scroll?: boolean }) => void
}

export function useQuinceRouter(): QuinceRouter {
  const router = useRouter()

  const push = (to: string, options?: { shallow?: boolean; scroll?: boolean }) => {
    if (shouldFullReload(to)) window.location.href = to
    else {
      void router.push(to, to, options)
    }
  }

  const replace = (to: string, as?: string, options?: { shallow?: boolean; scroll?: boolean }) => {
    if (shouldFullReload(to)) window.location.href = to
    else {
      void router.replace(to, as ?? to, options) // as is optional in next/router
    }
  }

  return {
    ...router,
    push,
    replace,
  }
}
